import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { TextField, Button } from '@frontierag/frontier-ui-library';
import { useField } from 'formik';

import useStyles from './styles';
import type { ITextareaField } from './types';

export default function TextareaField({
  name,
  variant = 'outlined',
  charLimit = 391,
  disabled,
  InputProps,
  ...props
}: ITextareaField) {
  const [{ value }, meta, { setValue, setTouched }] = useField(name);
  const { t } = useTranslation();
  const styles = useStyles();

  const handleOnChange = useCallback(
    async (event) => {
      await setValue(event.target.value);
    },
    [setValue],
  );

  const handleOnBlur = useCallback(async () => {
    if (!meta.touched) await setTouched(true, false);
  }, [setTouched, meta.touched]);

  const hasError = Boolean(meta.touched && meta.error);
  const helperText = hasError ? meta.error : `${value?.length}/${charLimit}`;

  const onClear = useCallback(async () => {
    await setValue('');
  }, [setValue]);

  const iProps = Object.assign(
    {
      inputProps: {
        sx: {
          resize: 'vertical',
        },
        maxLength: charLimit,
      },
    },
    {
      endAdornment: (
        <React.Fragment>
          {!disabled && (
            <Button
              variant="text"
              sx={styles.clear}
              color="primary"
              disabled={!value}
              size="small"
              onClick={onClear}
            >
              {t('buttons.clear')}
            </Button>
          )}
        </React.Fragment>
      ),
    },
    InputProps,
  );

  return (
    <TextField
      name={name}
      data-testid={`textarea-field-${name}`}
      variant={variant}
      multiline
      rows={1}
      disabled={disabled}
      value={value || ''}
      onChange={handleOnChange}
      onBlur={handleOnBlur}
      InputProps={iProps}
      InputLabelProps={{
        shrink: true,
      }}
      error={hasError}
      helperText={helperText}
      fullWidth
      sx={styles.textField}
      {...props}
    />
  );
}
